import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import SingleStreamView from "./SingleStreamView";
import ResultStreamDataTable from "./result_stream_dataTable";
import ResultComponentTable from "./result_stream_component_table";
import "./calculateTask3LG.less";
import { Button, Dropdown, Checkbox, Spin, Select, Input } from "antd";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import { designValue, getDesignValue, actList, getActValue } from "@/services/design";
import userInfo from "@/Components/user";
import { getJob, jobs, getSubJobs, getSubJob } from "@/services/common";
import dayjs from "dayjs";
import { useCompareData } from "../ResultPageFrame";
import materialsDB from "@/constants/materialsDB";
import { rebuildFormula } from "@/config/func";

// 引擎返回的 JSON 文本可能包含非法 NaN（与结果抽屉解析一致），解析前统一替换为 0，避免 JSON.parse 崩溃
const jsonParseSafe = (str: any, fallback: any = {}) => {
  if (typeof str !== "string") return str ?? fallback;
  try {
    return JSON.parse(str.replace(/NaN/g, "0"));
  } catch (e) {
    try {
      return JSON.parse(str);
    } catch (e2) {
      return fallback;
    }
  }
};

// 参考 DesignValue 的摩尔流量换算：为设计值流股计算 f_mol_per_h
const enrichDesignValueMolarFlow = (data: any) => {
  if (!data?.values?.mix_input?.streams) return data;
  const casToMW: Record<string, number> = {};
  materialsDB.forEach((mat: any) => {
    if (mat.cas) casToMW[mat.cas] = mat.MW;
  });
  const enriched = { ...data };
  enriched.values = { ...data.values };
  enriched.values.mix_input = { ...data.values.mix_input };
  enriched.values.mix_input.streams = data.values.mix_input.streams.map((s: any) => {
    if (s.f_mol_per_h && s.f_mol_per_h > 0) return s;
    const mole_frac_arr = s.materials?.map((m: any) => m.molar_frac || 0) || [];
    const cas_arr = s.materials?.map((m: any) => m.cas) || [];
    let avgMolarMass = 0;
    mole_frac_arr.forEach((mf: number, idx: number) => {
      const mw = casToMW[cas_arr[idx]] || 0;
      avgMolarMass += (mw / 1000) * mf;
    });
    const mass_flow = s.params?.f || 0;
    const f_mol_per_h = avgMolarMass > 0 ? mass_flow / avgMolarMass : 0;
    return { ...s, f_mol_per_h };
  });
  return enriched;
};

// 🔴 改造：按流股名称，把所有对比数据合并到 calcStream 上
const mergeCompareWithCalc = (calcStreams: any[], compareItems: any[]) => {
  return calcStreams.map((calcStream: any) => {
    const compareData = compareItems.map((item) => {
      // 优先用各页面统一上报的输出换算数据（fullOutput），否则回退 mix_input
      const stream = item?.values?.fullOutput?.streams?.find((s: any) => s.name === calcStream.name)
        || item?.values?.mix_input?.streams?.find((s: any) => s.name === calcStream.name);
      return {
        ...item,
        stream,
      };
    });
    return {
      ...calcStream,
      compareData, // 所有对比项的流股数据，按 compareItems 顺序排列
    };
  });
};

const CalculateTask3LG= ({ input, results, job_id,subJobid, type, item_name, streamDescMap, streamNodeMap, elementDescMap, tempUnit, pressUnit, setTempUnit, setPressUnit, activeElementName }) => {
  const params = useParams<{ k: string; f: string; j: string }>();
  const calcStreams = results?.streams ?? [];

  // 流股选择
  const [selectedStreams, setSelectedStreams] = useState<string[]>([]);
  // 对比数据相关
  const [dataList, setDataList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<"all" | "design" | "actual" | "task" | "otherCalc">("all");
  // 对比数据由父组件 Context 统一管理（与单元操作/输入参数同步）
  const { compareItems, setCompareItems } = useCompareData();
  // 对比项变更时同步下拉勾选状态（三处页面共享同一份对比状态）
  useEffect(() => {
    setSelectedIds(compareItems.map((item: any) => item.id));
  }, [compareItems]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [taskDataList, setTaskDataList] = useState<any[]>([]); // 任务数据列表
  const [confirmLoading, setConfirmLoading] = useState(false); // 确认选择时的 loading 状态
  const [otherCalcResults, setOtherCalcResults] = useState<any[]>([]); // 当前元件其他计算结果
  const [otherCalcLoading, setOtherCalcLoading] = useState(false); // 加载其他计算结果的状态
  // 实际值存储列表（actList 接口获取，[{ id, name }]）
  const [actListData, setActListData] = useState<any[]>([]);

  // 运行数据的 molar_frac 是对象数组 [{ "H₂S": 0.000003 }, ...]，且不一定覆盖所有组分；
  // 按输入组分顺序映射为 materials 数组，没有对应值的组分摩尔分率为 null（显示为 "-"）
  const buildTimeRangeMaterials = (s: any) => {
    const refStream = (input?.inputs?.streams || []).find((item: any) => item?.materials?.length);
    const inputMats = refStream?.materials || [];
    const fracMap: Record<string, number> = {};
    (s.molar_frac || []).forEach((item: any) => {
      if (item && typeof item === "object") {
        Object.entries(item).forEach(([name, v]) => {
          if (v != null) fracMap[name] = Number(v);
        });
      }
    });
    return inputMats.map((mat: any) => {
      const key = mat?.name ? rebuildFormula(String(mat?.name)) : "";
      const v = fracMap[key] ?? fracMap[mat?.name];
      return { molar_frac: v ?? null };
    });
  };
  const [showAllData, setShowAllData] = useState(true); // 是否显示全部数据（含无效数据）
  // 流股名筛选（左侧流股菜单顶部的搜索框）
  const [streamSearch, setStreamSearch] = useState<string>("");
  // 源/目标筛选（右侧顶部）
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [targetFilter, setTargetFilter] = useState<string>("");

  // 源/目标匹配：源和目标都有值时，满足其一即视为命中（分别满足的流股都展示）
  const matchStreamFilter = (s: any, sf: string, tf: string) => {
    const from = streamNodeMap?.[s.name]?.fromName || "";
    const to = streamNodeMap?.[s.name]?.toName || "";
    const hitFrom = !sf || from.includes(sf);
    const hitTo = !tf || to.includes(tf);
    return sf && tf ? from.includes(sf) || to.includes(tf) : hitFrom && hitTo;
  };

  // 左侧菜单展示的流股：流股名筛选、源/目标筛选（都是筛选条件，同时有值时取交集）；
  // 与「筛选流股」一致 —— 筛完左侧只留下命中的流股，它们会被下面的 effect 自动勾选，
  // 因此菜单里看到的就是全部勾选的流股
  const searchedCalcStreams = useMemo(() => {
    const ns = streamSearch.trim();
    const sf = sourceFilter.trim();
    const tf = targetFilter.trim();
    if (!ns && !sf && !tf) return calcStreams;
    return calcStreams.filter((s: any) => {
      const hitName = !ns || s.name.includes(ns);
      const hitNode = matchStreamFilter(s, sf, tf);
      return hitName && hitNode;
    });
  }, [calcStreams, streamSearch, sourceFilter, targetFilter, streamNodeMap]);

  // 当前可见列表是否全部勾选
  const allVisibleChecked =
    searchedCalcStreams.length > 0 &&
    searchedCalcStreams.every((s: any) => selectedStreams.includes(s.name));

  // 初始化全选第一个流股
  useEffect(() => {
    if (calcStreams.length > 0) {
      setSelectedStreams(calcStreams.map((s: any) => s.name));//初始化全选
      // setSelectedStreams([calcStreams[0].name]);
    }
  }, [calcStreams]);

  // 从单元操作页跳转而来时：默认选中该元件的输入/输出流股（后执行覆盖上面的全选）
  useEffect(() => {
    if (!activeElementName || !streamNodeMap || calcStreams.length === 0) return;
    const related = calcStreams
      .filter((s: any) => {
        const node = streamNodeMap[s.name];
        return node && (node.fromName === activeElementName || node.toName === activeElementName);
      })
      .map((s: any) => s.name);
    if (related.length > 0) {
      setSelectedStreams(related);
    }
  }, [activeElementName, calcStreams, streamNodeMap]);

  // 全选/取消全选（作用于左侧当前可见列表：有筛选时只勾选筛出的流股）
  const handleSelectAll = () => {
    if (allVisibleChecked) {
      const visibleNames = new Set(searchedCalcStreams.map((s: any) => s.name));
      setSelectedStreams((prev) => prev.filter((n) => !visibleNames.has(n)));
    } else {
      const visibleNames = searchedCalcStreams.map((s: any) => s.name);
      setSelectedStreams((prev) => {
        const next = [...prev];
        visibleNames.forEach((n) => {
          if (!next.includes(n)) next.push(n);
        });
        return next;
      });
    }
  };

  // 单选/取消单选
  const toggleStream = (name: string) => {
    setSelectedStreams((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  // 单选模式
  const handleSingleSelect = (name: string) => {
    setSelectedStreams([name]);
  };

  // 合并计算值 + 所有对比数据
  const mergedStreams = useMemo(() => {
    const filtered = calcStreams.filter((s: any) => selectedStreams.includes(s.name));
    return mergeCompareWithCalc(filtered, compareItems);
  }, [results, selectedStreams, compareItems]);

  // 下拉候选：所有流股的源设备 / 目标设备名
  const sourceOptions = useMemo(() => {
    const set = new Set<string>();
    calcStreams.forEach((s: any) => {
      const from = streamNodeMap?.[s.name]?.fromName;
      if (from) set.add(from);
    });
    return Array.from(set);
  }, [calcStreams, streamNodeMap]);

  const targetOptions = useMemo(() => {
    const set = new Set<string>();
    calcStreams.forEach((s: any) => {
      const to = streamNodeMap?.[s.name]?.toName;
      if (to) set.add(to);
    });
    return Array.from(set);
  }, [calcStreams, streamNodeMap]);

  // 展示的流股 = 左侧勾选的流股。
  // 流股名/源/目标筛选只负责改写左侧菜单的可见列表与勾选（见 searchedCalcStreams），
  // 不在此处二次过滤，这样筛选之后再手动勾选的流股也会正常显示
  const filteredStreams = mergedStreams;

  // 筛选生效时，左侧勾选同步为左侧菜单里展示的流股：筛完菜单里只剩勾选项
  useEffect(() => {
    if (!streamSearch.trim() && !sourceFilter.trim() && !targetFilter.trim()) return; // 无筛选时保留左侧手动勾选状态
    setSelectedStreams(searchedCalcStreams.map((s: any) => s.name));
  }, [searchedCalcStreams, streamSearch, sourceFilter, targetFilter]);

  // 判断是否为单选状态
  const isSingleStream = mergedStreams.length === 1;
  const singleStreamData = isSingleStream ? mergedStreams[0] : null;

  // 获取对比数据列表
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await designValue({ flow_id: params.f }, userInfo.access_token);
      setDataList(res.data || []);
    } catch (err) {
      console.error("获取对比数据失败", err);
    } finally {
      setLoading(false);
    }
  };
  // 获取任务数据
      const fetchTaskData = async () => {
          setLoading(true);
          try {
              const res = await jobs({ flow_id: params.f, page: 1, perPage: 1000 }, userInfo.access_token);
              const allTaskData = res.data.data || [];
            const filteredData = params.j || subJobid
                ? allTaskData.filter((item: any) => String(item.id) !== String(params.j || subJobid))
                : allTaskData;
            // 过滤掉计算失败或未计算的任务（status 不是 "done" 的排除）
            const successData = filteredData.filter((item: any) => item.status === "done");
            setTaskDataList(successData);
          } catch (err) {
              console.error("获取任务数据失败", err);
          } finally {
              setLoading(false);
          }
      };

  // 获取当前元件其他计算结果
  const fetchOtherCalcResults = async () => {
    if (type !== 'subJob' || !item_name) return;
    setOtherCalcLoading(true);
    try {
      const res = await getSubJobs(
        {
          job_id: subJobid,  // 使用 flow_id 而不是 job_id
          name: item_name,
        },
        userInfo.access_token,
      );
      if (res.status === 200) {
        // 过滤掉当前任务，并且过滤掉计算失败或未计算的结果
        const filteredResults = res.data.filter((item: any) => 
          String(item.id) !== String(params.j || job_id) && item.status === "done"
        );
        setOtherCalcResults(filteredResults);
      }
    } catch (err) {
      console.error("获取其他计算结果失败", err);
    } finally {
      setOtherCalcLoading(false);
    }
  };

  // 获取当前组合计算的其他运行结果（type=multiJobs：同组合名多次运行中，除当前运行外的其他次）
  const fetchComboOtherCalcResults = async () => {
    if (type !== 'multiJobs') return;
    setOtherCalcLoading(true);
    try {
      const res = await getSubJobs({ job_id: subJobid }, userInfo.access_token);
      if (res.status === 200) {
        const list = res.data || [];
        // 当前运行所在组合名：以当前 subjob(id=job_id) 的行记录为准
        const cur = list.find(
          (item: any) => String(item.id) === String(job_id) && item.type === 'multi',
        );
        if (!cur) {
          setOtherCalcResults([]);
          return;
        }
        const filteredResults = list.filter(
          (item: any) =>
            item.type === 'multi' &&
            item.name === cur.name &&
            String(item.id) !== String(job_id) &&
            item.status === 'done',
        );
        setOtherCalcResults(filteredResults);
      }
    } catch (err) {
      console.error("获取组合计算其他运行结果失败", err);
    } finally {
      setOtherCalcLoading(false);
    }
  };

  // 获取实际值存储列表
  const fetchActList = async () => {
    try {
      const res = await actList(userInfo.access_token);
      // 返回结构为 { records: [{ id, name }] }
      setActListData(res.data?.records || []);
      console.log("实际值存储列表:", res.data);
    } catch (err) {
      console.error("获取实际值存储列表失败", err);
      setActListData([]);
    }
  };

  // 下拉打开时获取数据
  const handleDropdownOpen = (open: boolean) => {
    setDropdownOpen(open);
    if (open) {
      fetchData();
      fetchTaskData();
      fetchActList();
      if (type === 'subJob' && item_name) {
        fetchOtherCalcResults();
      } else if (type === 'multiJobs') {
        fetchComboOtherCalcResults();
      }
    }
  };

 // 筛选（包括设计值、实际值存储项、任务数据和其他计算结果）
     const filteredList = useMemo(() => {
         // 实际值存储项：actList 接口返回的存储工况记录
         const actItems = actListData.map((rec) => ({
           id: `act-${rec.id}`,
           storeId: rec.id,
           name: rec.name,
           source: 'act',
         }));
         if (filterType === "all") {
             // 全部：显示设计值 + 实际值 + 任务数据
             return [
                 ...dataList.map(item => ({ ...item, source: 'design' })),
                 ...actItems,
                 ...taskDataList.map(item => ({ ...item, source: 'task' })),
                 ...otherCalcResults.map(item => ({ ...item, source: 'otherCalc' })),
             ];
         } else if (filterType === "design") {
             return dataList.filter(item => item.type === "design").map(item => ({ ...item, source: 'design' }));
         } else if (filterType === "actual") {
             return actItems;
         } else if (filterType === "task") {
             return taskDataList.map(item => ({ ...item, source: 'task' }));
         } else if (filterType === "otherCalc") {
             // 当前元件其他计算结果
             return otherCalcResults.map(item => ({ ...item, source: 'otherCalc' }));
         }
         return [];
     }, [dataList, taskDataList, otherCalcResults, filterType, actListData]);

  // 多选切换
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // 确认选择对比项
  const handleConfirm = async () => {
          setConfirmLoading(true); // 开始 loading
          try {
              const fetchPromises = selectedIds.map(async (id) => {
                  try {
                      // 在所有数据源中查找对应的项目，判断来源（不依赖 filteredList）
                      const taskItem = taskDataList.find(item => String(item.id) === String(id));
                      const otherCalcItem = otherCalcResults.find(item => String(item.id) === String(id));
                      const actRec = actListData.find(r => `act-${r.id}` === id);

                      if (actRec) {
                          // 实际值存储项：通过 getActValue 获取存储的实际值数据
                          const res = await getActValue(actRec.id, userInfo.access_token);
                          const actData = res.data.result; // 详情数据（结构同 realtime 返回的 { input, output }）
                          console.log("实际值存储详情:", actData);
                          const outputData = actData.output;
                          const inputData = actData.input;

                          // 与任务数据一致，直接用输出数据构建对比格式
                          const convertedStreams = (outputData.streams || []).map((s: any) => ({
                            name: s.name,
                            params: {
                              f: s.f_kg_per_h,
                              f_unit: "kg/h",
                              t: s.T_C,
                              t_unit: "C",
                              p: s.P_MPa,
                              p_unit: "MPa",
                            },
                            materials: buildTimeRangeMaterials(s),
                            // vf: s.vf,
                            // H: s.H,
                            mass_frac: s.mass_frac,
                            // f_mol_per_h: s.f_mol_per_h,
                            f_nm3_per_h: s.f_nm3_per_h,
                            f_m3_per_h: s.f_m3_per_h,
                            f_l_per_h: s.f_l_per_h,
                          }));

                          return {
                              id,
                              values: {
                                name: actRec.name,
                                mix_input: { streams: convertedStreams, elements: outputData.elements || [] },
                                fullInput: inputData, // 完整输入数据，供输入参数页对比展示
                                rawInput: inputData, // 接口返回的原始 input
                                rawOutput: outputData, // 接口返回的原始 output
                              },
                              type: "actual",
                              name: actRec.name,
                          };
                      } else if (taskItem) {
                          // 任务数据：使用 getJob 接口
                          const res = await getJob(id, userInfo.access_token);
                          // 过滤掉计算失败或未计算的任务
                          if (res.data.status !== "done") return null;
                          const outputData = jsonParseSafe(res.data.output);
                          const inputData = jsonParseSafe(res.data.input);

                          // 直接用输出数据构建对比格式，与当前计算结果保持一致
                          const convertedStreams = (outputData.streams || []).map((s: any) => ({
                            name: s.name,
                            params: {
                              f: s.f_kg_per_h ?? 0,
                              f_unit: "kg/h",
                              t: s.T_C ?? 0,
                              t_unit: "C",
                              p: s.P_MPa ?? 0,
                              p_unit: "MPa",
                            },
                            materials: (s.molar_frac || []).map((mf: number) => ({
                              molar_frac: mf ?? 0,
                            })),
                            vf: s.vf,
                            H: s.H,
                            mass_frac: s.mass_frac,
                            f_mol_per_h: s.f_mol_per_h,
                            f_nm3_per_h: s.f_nm3_per_h,
                            f_m3_per_h: s.f_m3_per_h,
                            f_l_per_h: s.f_l_per_h,
                          }));

                          const data = {
                            id: res.data.id,
                            values: {
                              name: res.data.name,
                              mix_input: { streams: convertedStreams, elements: outputData.elements || [] },
                              fullInput: inputData, // 完整输入数据，供输入参数页对比展示
                            },
                            type: "task",
                          };
                          return data;
                      } else if (otherCalcItem) {
                          // 当前元件其他计算结果：使用 getSubJob 接口
                          const res = await getSubJob(id, userInfo.access_token);
                          // 过滤掉计算失败或未计算的任务
                          if (res.data.status !== "done") return null;
                          const outputData = jsonParseSafe(res.data.output);
                          const inputData = jsonParseSafe(res.data.input);

                          // 直接用输出数据构建对比格式，与当前计算结果保持一致
                          const convertedStreams = (outputData.streams || []).map((s: any) => ({
                            name: s.name,
                            params: {
                              f: s.f_kg_per_h ?? 0,
                              f_unit: "kg/h",
                              t: s.T_C ?? 0,
                              t_unit: "C",
                              p: s.P_MPa ?? 0,
                              p_unit: "MPa",
                            },
                            materials: (s.molar_frac || []).map((mf: number) => ({
                              molar_frac: mf ?? 0,
                            })),
                            vf: s.vf,
                            H: s.H,
                            mass_frac: s.mass_frac,
                            f_mol_per_h: s.f_mol_per_h,
                            f_nm3_per_h: s.f_nm3_per_h,
                            f_m3_per_h: s.f_m3_per_h,
                            f_l_per_h: s.f_l_per_h,
                          }));

                          const data = {
                            id: res.data.id,
                            values: {
                              name: res.data.name,
                              mix_input: { streams: convertedStreams, elements: outputData.elements || [] },
                              fullInput: inputData, // 完整输入数据，供输入参数页对比展示
                            },
                            created_at: res.data.created_at,
                            type: "otherCalc",
                          };
                          return data;
                      } else {
                          // 设计值/实际值：使用 getDesignValue 接口
                          const res = await getDesignValue(id, userInfo.access_token);
                          console.log("设计值/实际值:", res.data);
                          return enrichDesignValueMolarFlow(res.data);
                      }
                  } catch (err) {
                      console.error("获取失败", id, err);
                      return null;
                  }
              });
              // 2. 等待所有请求完成
              let selectedItems = await Promise.all(fetchPromises);
              // 3. 过滤掉失败的
              selectedItems = selectedItems.filter(Boolean);
              console.log("最终对比数据 compareItems:", selectedItems);
              setCompareItems(selectedItems);
              setDropdownOpen(false);
          } finally {
              setConfirmLoading(false); // 结束 loading
          }
      };

  // 下拉面板内容
  const dropdownContent = (
    <div
      style={{
        width: type === 'subJob' ? 470 : 320,
        padding: 12,
        // maxHeight: 400,
        // overflow: "auto",
        boxSizing: "border-box",
        backgroundColor: "#f0efefff",
      }}
    >
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        <Button
          size="small"
          type={filterType === "all" ? "primary" : "default"}
          onClick={() => setFilterType("all")}
        >
          全部
        </Button>
        <Button
          size="small"
          type={filterType === "design" ? "primary" : "default"}
          onClick={() => setFilterType("design")}
        >
          设计值
        </Button>
        <Button
          size="small"
          type={filterType === "actual" ? "primary" : "default"}
          onClick={() => setFilterType("actual")}
        >
          运行数据
        </Button>
        <Button
          size="small"
          type={filterType === "task" ? "primary" : "default"}
          onClick={() => setFilterType("task")}
        >
          其他任务
        </Button>
        {type === 'subJob' && (
          <Button
            size="small"
            type={filterType === "otherCalc" ? "primary" : "default"}
            onClick={() => {
              setFilterType("otherCalc");
              fetchOtherCalcResults();
            }}
          >
            当前元件其他任务
          </Button>
        )}
        {type === 'multiJobs' && (
          <Button
            size="small"
            type={filterType === "otherCalc" ? "primary" : "default"}
            onClick={() => {
              setFilterType("otherCalc");
              fetchComboOtherCalcResults();
            }}
          >
            当前组合计算其他任务
          </Button>
        )}
      </div>

      <div style={{maxHeight:350,overflow: "auto"}}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <Spin />
          </div>
        ) : filteredList.length === 0 ? (
          <div style={{ textAlign: "center", color: "#999" }}>暂无数据</div>
        ) : (
          filteredList.map((item) => (
            <div
              key={item.id}
              style={{
                padding: "8px 10px",
                border: "1px solid #eee",
                borderRadius: 4,
                marginBottom: 6,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>
                {item.source === 'task' 
                  ?  `${item.name || item.id}`
                  : item.source === 'otherCalc'
                  ? `${item.name || item.id} (${item.created_at ? dayjs(item.created_at).add(8, "hours").format("YYYY-MM-DD HH:mm:ss") : ''})`
                  : item.source === 'act'
                  ? `${item.name}`
                  : `${item.type === "design" ? "设计值" : "运行数据"} ${item.id}`
                }
              </span>
              <Checkbox
                checked={selectedIds.includes(item.id)}
                onChange={() => toggleSelect(item.id)}
              />
            </div>
          ))
        )}
      </div>

      <div
        style={{
          marginTop: 10,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ color: "#666" }}>已选：{selectedIds.length} 项</span>
        <Button type="primary" size="small" onClick={handleConfirm} loading={confirmLoading}>
          {confirmLoading ? "正在对比..." : "确认选择"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="calculate-task-containerLg">
      <div className="main-layout">
        {/* 左侧流股选择栏 */}
        <div className="stream-list-sidebar">
          <div className="sidebar-header">
            <Input
              size="small"
              allowClear
              prefix={<SearchOutlined style={{ color: "#999" }} />}
              placeholder="筛选流股名称"
              value={streamSearch}
              onChange={(e) => setStreamSearch(e.target.value)}
              style={{ width: "100%", marginBottom: 6 }}
            />
            <label className="select-all-label">
              <input
                type="checkbox"
                checked={allVisibleChecked}
                onChange={handleSelectAll}
              />
              <span>全选</span>
            </label>
          </div>
          <div className="stream-list">
            {searchedCalcStreams.length === 0 ? (
              <div style={{ padding: 10, color: "#999", fontSize: 13 }}>无匹配流股</div>
            ) : (
              searchedCalcStreams.map((s: any) => (
                <div
                  key={s.name}
                  className={`stream-item ${selectedStreams.includes(s.name) ? "active" : ""}`}
                  onClick={(e) => {
                    // handleSingleSelect(s.name);
                    // e.stopPropagation();
                    toggleStream(s.name)
                  }}
                >
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedStreams.includes(s.name)}
                      onChange={() => toggleStream(s.name)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </label>
                  <span className="stream-name">{s.name}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 右侧内容区 */}
        <div className="data-content-area" style={{ position: "relative" }}>
          {/* 顶部按钮组 */}
          <div style={{ position: "absolute", top: "10px", right: "40px", display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 14 }}>温度：</span>
            <Select value={tempUnit} onChange={setTempUnit} style={{ width: 60 }} size="small"
              options={[{ value: "℃", label: "℃" }, { value: "K", label: "K" }]} />
            <span style={{ fontSize: 14 }}>压力：</span>
            <Select value={pressUnit} onChange={setPressUnit} style={{ width: 80 }} size="small"
              options={[{ value: "Pa", label: "Pa" }, { value: "bar", label: "bar" }, { value: "KPa", label: "KPa" }, { value: "MPa", label: "MPa" }, { value: "atm", label: "atm" }]} />
            <Dropdown
              overlay={dropdownContent}
              trigger={["click"]}
              open={dropdownOpen}
              onOpenChange={handleDropdownOpen}
              getPopupContainer={() => document.body}
              overlayStyle={{ zIndex: 100002 }}
              placement="bottomRight"
            >
              <Button type="primary">
                对比设计值/运行数据 <DownOutlined />
              </Button>
            </Dropdown>
          </div>

          {/* 已选对比项提示 */}
          {/* {compareItems.length > 0 && (
            <div style={{ position: "absolute", top: "50px", right: "40px", color: "#1890ff" }}>
              正在对比：
              {compareItems
                .map((item) => `${item.type === "design" ? "设计值" : "实际值"}(${item.id})`)
                .join("、")}
            </div>
          )} */}

          {/* {isSingleStream && singleStreamData ? (
            // 单选：卡片式布局
            <SingleStreamView stream={singleStreamData} input={input} />
          ) : ( */}
            {/* // 多选：表格布局 */}
            <>
              <div className="data-section">
                <ResultStreamDataTable streams={filteredStreams} compareItems={compareItems}  input={input} streamDescMap={streamDescMap} streamNodeMap={streamNodeMap} elementDescMap={elementDescMap} tempUnitProp={tempUnit} pressUnitProp={pressUnit}
                  sourceFilter={sourceFilter} setSourceFilter={setSourceFilter} sourceOptions={sourceOptions}
                  targetFilter={targetFilter} setTargetFilter={setTargetFilter} targetOptions={targetOptions} />
              </div>
              <div className="data-section">
                <ResultComponentTable streams={filteredStreams} input={input} compareItems={compareItems} showAllData={showAllData} streamDescMap={streamDescMap} streamNodeMap={streamNodeMap} elementDescMap={elementDescMap} />
              </div>
            </>
          {/* )} */}
        </div>
      </div>
    </div>
  );
};

export default CalculateTask3LG;